import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import MediaUpload from "../../../components/Forms/MediaUpload";
import RichTextEditor from "../../../components/Forms/RichTextEditor";
import { ActionButton } from "../../../components/Forms/ActionButton";
import { useCreateNews, useGetNewsById, useUpdateNews } from "../../../queries/knowledgeBoard";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useLocation, useNavigate } from "react-router-dom";

const CreateNews = () => {

    const navigate = useNavigate();

    const { state } = useLocation();
    const id = state?.id;
    
    const [active, setActive] = useState(true);
    const [coverImage, setCoverImage] = useState(null);
    const [fileId, setFileId] = useState(null);
    const [mediaClear, setMediaClear] = useState(false);

    const { data, isLoading } = useGetNewsById(id);
    
    const { mutateAsync: create, isPending: isCreating } = useCreateNews();

    const { mutateAsync: update, isPending: isUpdating } = useUpdateNews();

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        getValues,
        reset,
    } = useForm({
        defaultValues: {
            title: data ? data.title : "",
            description: data ? data.description : "",
        }
    });

    const [editorResetKey, setEditorResetKey] = useState(0); // Add this state to track resets

    const onSubmit = async (data) => {
        if (!coverImage) {
            alert("Please upload a thumbnail.");
            return;
        }
        if(getValues('description') === "") {
            alert("Please write a description.");
            return;
        }
        console.log({
            ...data,
            isActive: active,
            file: coverImage || "",
        });
        if(id){
            toast.loading('Updating news...');
            await update({
                id: id,
                title: data.title,
                description: data.description,
                source: data.source,
                link: data.link,
                isActive: active,
                file: coverImage || "",
                fileId: fileId,
            }).then(() => {
                toast.dismiss();
                toast.success("News updated successfully!");
                setTimeout(() => {
                    navigate('/knowledge-board/news/list-news');
                }, 2000);
            }).catch((error) => {
                toast.dismiss();
                console.error("Error updating news:", error);
                toast.error("Error updating news. Please try again.", error?.res?.data?.error);
            });
        } else {
            toast.loading('Creating news...');
            await create({
                title: data.title,
                description: data.description,
                source: data.source,
                link: data.link,
                isActive: active,
                file: coverImage || "",
            }).then(() => {
                toast.dismiss();
                toast.success("News created successfully!");
                reset();
                setCoverImage(null);
                setTimeout(() => {
                    navigate('/knowledge-board/news/list-news');
                }, 2000);
            }).catch((error) => {
                toast.dismiss();
                console.error("Error creating news:", error);
                toast.error("Error creating news. Please try again.", error?.res?.data?.error);
            });
        }
    }

    useEffect(() => {
        if (data) {
            setValue("title", data.title);
            setValue("description", data.description || ""); // Add fallback value
            setValue("source", data.source);
            setValue("link", data.link);
            setActive(data.isActive);
            setCoverImage(data.imageUrl);
            setFileId(data.fileId || data.imageId);
        }
    }, [data, setValue]); // Added setValue to dependencies
    
    useEffect(() => {
        setTimeout(() => {
            if (data) {
                setCoverImage(data.imageUrl);
                setValue("description", data.description || "");
            }
        }, 1000);
    }, [data]);

    useEffect(() => {
        setMediaClear(false);
    }, [coverImage]);
    
    if (isLoading) {
        return <div className="flex justify-center items-center h-screen">
            <p className="text-lg font-semibold">Loading...</p>
        </div>
    }
    
    return (
        <form className="flex flex-col w-full font-semibold" onSubmit={handleSubmit(onSubmit)}>
            <section className="flex flex-col justify-center px-5 pt-2 py-9 gap-3  w-full bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)]">
                <div className="flex items-center justify-between">
                    <p className="text-base font-bold mt-3">News Information</p>
                    <div className="flex gap-3 items-center">
                        <p className="text-base">Status:</p>
                        <div className={`flex justify-end items-center border ${active ? `border-primary` : `border-inactive`} rounded-md h-full min-h-8 min-w-20`}>
                            <label className="inline-flex items-center me-5 cursor-pointer h-full">
                                <span className={`ms-3 text-base ${active ? `text-primary` : 'text-inactive'} font-bold dark:text-gray-300 mr-2`}>{active ? 'Active' : 'Inactive'}</span>
                                <input type="checkbox" name={'active'} value={active} className="sr-only peer" onChange={() => setActive(!active)} checked={active} />
                                <div className="relative w-8 h-4 bg-white border-black ring-1 ring-inactive rounded-full peer dark:bg-white peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.1 after:start-[0px] after:bg-inactive after:border-inactive peer-checked:after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-navBtn peer-checked:ring-primary peer-checked:bg-primary"></div>
                            </label>
                        </div>
                    </div>
                </div>

                <div className="flex gap-3">
                    <div className="flex flex-col w-full gap-4">
                        {/* Title */}
                        <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Title of News</label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="text"
                                    placeholder="News Title"
                                    {...register("title", { required: "Title is required" })}
                                    className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.title ? "border-red-500" : ""}`}
                                />
                            </div>
                            {errors.title && <p className="text-red text-sm">{errors.title.message}</p>}
                        </div>
                        {/* Source */}
                        <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">News Source</label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="text"
                                    placeholder="News Source"
                                    {...register("source", { required: "Source is required" })}
                                    className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.source ? "border-red-500" : ""}`}
                                />
                            </div>
                            {errors.source && <p className="text-red text-sm">{errors.source.message}</p>}
                        </div>
                        {/* Description */}
                        <div className="mt-2">
                            <RichTextEditor
                                key={`editor-${editorResetKey}`}  // Changed this to use the reset counter
                                label="Description"
                                placeholder="Write your overview here..."
                                name="description"
                                value={getValues("description") || ""}  // Ensure empty string fallback
                                onChange={(value) => setValue("description", value)}
                                error={errors.description}
                            />
                            {errors.description && <p className="text-red text-sm">{errors.description.message}</p>}  {/* Fixed error message */}
                        </div>
                    </div>
                    {/* Cover Image */}
                    <div className="mt-0">
                        <MediaUpload
                            key={coverImage || "new"}  // Added key prop to force re-render
                            clearMedia={mediaClear}
                            label={"Thumbnail"}
                            existingImage={coverImage}
                            setImageUpload={setCoverImage}
                            stateImage={coverImage}
                            setStateImageUpload={setCoverImage}
                            useFileObject={true}
                        />
                    </div>
                </div>
                {/* Link */}
                <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                    <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Link</label>
                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                        <input
                            type="text"
                            placeholder="Resource Link"
                            {...register("link", { required: "Link is required" })}
                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.link ? "border-red-500" : ""}`}
                        />
                    </div>
                    {errors.link && <p className="text-red text-sm">{errors.link.message}</p>}
                </div>

                <div className="mt-10 flex gap-5 items-center justify-between">
                    <ActionButton
                        variant="reset"
                        text="Reset"
                        handleSubmit={() => {
                            setMediaClear(!mediaClear);
                            setCoverImage(null);
                            reset({
                                title: "",
                                description: "",
                                source: "",
                                link: ""
                            });
                            setEditorResetKey(prev => prev + 1);
                            setActive(true);
                        }}
                    />
                    <div className="flex items-center gap-3">
                        <ActionButton
                            variant="cancel"
                            text="Cancel"
                            handleSubmit={() => navigate('/knowledge-board/news/list-news')}
                        />
                        <ActionButton
                            variant="submit"
                            text="Save"
                            handleSubmit={() => { }}
                            disabled={isCreating || isUpdating}
                        />
                    </div>
                </div>
            </section>
            <ToastContainer
                position="top-right"
            />
        </form>
    );
}

export default CreateNews;